import { Link, NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  BookOpenCheck,
  History,
  Settings as SettingsIcon,
  Users,
  LogOut,
  ChevronDown,
  ShieldAlert,
  CreditCard,
  Crown,
  Menu,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { SystemCheck } from "@/components/SystemCheck";

const ROLE_LABEL = { readonly: "Read-Only Access · Actions Restricted", reviewer: "Reviewer · Can create & edit", admin: "Admin · Full control" };

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/playbooks", label: "Playbooks", icon: BookOpenCheck },
  { to: "/audit", label: "Audit Trail", icon: History },
  { to: "/admin", label: "Administration", icon: Users, adminOnly: true },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
  { to: "/platform", label: "Platform Admin", icon: Crown, platformOnly: true },
];


// App shell sidebar: brand block, navigation, workspace switcher, user footer.

function BrandBlock({ user, workspaces, currentWorkspace, currentRole, switchWorkspace, logout, navi, isPlatform, brand, platform, logoSrc, API_BASE, mobileNav, setMobileNav, wOpen, setWOpen, roleBadge }) {
  return (
        <div className="p-5 border-b border-slate-800/80 flex items-start justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="app-logo-link" onClick={() => setMobileNav(false)}>
            {brand ? (
              <>
                {brand.logo_url && <img src={`${API_BASE}${brand.logo_url}`} alt={brand.product_name || "Logo"} className="w-11 h-11 object-contain" data-testid="brand-logo" />}
                <div>
                  <div className="font-heading text-base font-extrabold leading-none text-slate-100" style={brand.primary_color ? { color: brand.primary_color } : undefined} data-testid="brand-name">{brand.product_name || "Document Review"}</div>
                  <div className="label-cap mt-0.5">{brand.tagline || `Powered by ${platform.product_name}`}</div>
                </div>
              </>
            ) : (
              <>
                <img src={logoSrc} alt={platform.product_name} className="w-11 h-11 object-contain mix-blend-screen" data-testid="platform-logo" />
                <div>
                  <div className="font-heading text-base font-extrabold leading-none text-slate-100" data-testid="platform-product-name">{/^steelforge\s?ai$/i.test(platform.product_name) ? <><span className="text-slate-100">STEEL</span><span className="text-amber-400">FORGE</span><span className="text-amber-400 text-xs">AI</span></> : platform.product_name}</div>
                  <div className="label-cap mt-0.5 truncate max-w-[160px]">{platform.tagline || "Document Intelligence"}</div>
                </div>
              </>
            )}
          </Link>
          <button className="lg:hidden text-slate-400 hover:text-slate-100 p-1" onClick={() => setMobileNav(false)} aria-label="Close menu" data-testid="mobile-nav-close"><X className="w-5 h-5" /></button>
        </div>

  );
}

function MainNav({ user, workspaces, currentWorkspace, currentRole, switchWorkspace, logout, navi, isPlatform, brand, platform, logoSrc, API_BASE, mobileNav, setMobileNav, wOpen, setWOpen, roleBadge }) {
  return (
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {nav.map((item) => {
            if (item.adminOnly && currentRole !== "admin") return null;
            if (item.platformOnly && !isPlatform) return null;
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileNav(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2.5 lg:py-2 rounded-md text-sm transition ${
                    isActive
                      ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40 border border-transparent"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
  );
}

function WorkspaceFooter({ user, workspaces, currentWorkspace, currentRole, switchWorkspace, logout, navi, isPlatform, brand, platform, logoSrc, API_BASE, mobileNav, setMobileNav, wOpen, setWOpen, roleBadge }) {
  return (

        <div className="p-3 border-t border-slate-800/80">
          <div className="sc-card p-3">
            <div className="label-cap mb-1">Workspace</div>
            <DropdownMenu open={wOpen} onOpenChange={setWOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  className="w-full flex items-center justify-between text-left text-sm font-semibold text-slate-100 hover:text-amber-400"
                  data-testid="workspace-selector-dropdown"
                >
                  <span className="truncate">{currentWorkspace?.name || "—"}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-slate-900 border-slate-700 text-slate-100 w-56">
                <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-800" />
                {workspaces.map((w) => (
                  <DropdownMenuItem
                    key={w.id}
                    onSelect={() => switchWorkspace(w.id)}
                    className="cursor-pointer focus:bg-slate-800"
                    data-testid={`workspace-option-${w.id}`}
                  >
                    <div className="flex justify-between w-full">
                      <span>{w.name}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono uppercase ${
                        { admin: "role-admin", reviewer: "role-reviewer", readonly: "role-readonly" }[w.role]
                      }`}>{w.role}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="mt-2 flex items-center gap-2">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono uppercase font-bold ${roleBadge}`} data-testid="current-role-badge">
                {currentRole}
              </span>
              <span className="text-xs text-slate-500 truncate">{user?.email}</span>
            </div>
            <div className="mt-2"><SystemCheck /></div>
          </div>
        </div>
  );
}

// App shell sidebar: brand block, navigation, workspace switcher, user footer.
export function Sidebar(props) {
  return (
    <>
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] transform transition-transform duration-200 lg:static lg:z-auto lg:w-64 lg:max-w-none lg:translate-x-0 border-r border-slate-800/80 bg-[#0e1420] flex flex-col ${props.mobileNav ? "translate-x-0" : "-translate-x-full"}`} data-testid="app-sidebar">
        <BrandBlock {...props} />
        <MainNav {...props} />
        <WorkspaceFooter {...props} />
      </aside>
    </>
  );
}
